/**
 * Collection filtering.
 *
 * The rail is a real GET form, so filtering still works with JavaScript disabled.
 * With JS available this upgrades to AJAX using Shopify's Section Rendering API:
 * `?section_id=<id>` returns just this section's markup, which is swapped in without
 * a full page reload. The URL is kept in sync via history.pushState so filtered
 * views stay shareable and the back button works.
 *
 * All listeners are delegated from `document`, so they survive the innerHTML swap
 * and never need rebinding.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-collection-filters]';
  var inFlight = null;
  var debounceTimer = null;

  function getForm() {
    return document.querySelector(SECTION_SELECTOR);
  }

  /* The stable wrapper Shopify renders around the section; innerHTML is replaced
     inside it, so it is safe to hold on to. */
  function getWrapper(form) {
    var id = form.getAttribute('data-section-id');
    return document.getElementById('shopify-section-' + id) || form.closest('.shopify-section');
  }

  function setLoading(state) {
    var form = getForm();
    if (!form) return;
    var results = form.querySelector('[data-results]');
    var loader = form.querySelector('[data-loader]');
    if (results) {
      results.classList.toggle('is-loading', state);
      results.setAttribute('aria-busy', state ? 'true' : 'false');
    }
    if (loader) loader.hidden = !state;
  }

  /** Serialize the rail, dropping blank inputs so they never reach the URL. */
  function urlFromForm(form) {
    var params = new URLSearchParams();
    var elements = form.querySelectorAll('input, select');

    Array.prototype.forEach.call(elements, function (el) {
      if (!el.name || el.disabled) return;
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) params.append(el.name, el.value);
        return;
      }
      var value = (el.value || '').trim();
      if (value !== '') params.append(el.name, value);
    });

    var base = form.getAttribute('action') || window.location.pathname;
    var query = params.toString();
    return query ? base + '?' + query : base;
  }

  /**
   * Fetch a filtered view and swap it in.
   * `push` is false when responding to popstate, so we do not stack history entries.
   */
  function render(url, push) {
    var form = getForm();
    if (!form) return;
    var wrapper = getWrapper(form);
    if (!wrapper) {
      window.location.href = url;
      return;
    }

    var sectionId = form.getAttribute('data-section-id');
    var parts = url.split('?');
    var sectionUrl = parts[0] + '?section_id=' + encodeURIComponent(sectionId) + (parts[1] ? '&' + parts[1] : '');

    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    setLoading(true);

    fetch(sectionUrl, { signal: inFlight.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        /* The response is the section wrapper; take its contents. */
        var fresh = doc.querySelector('#shopify-section-' + sectionId) || doc.body;
        wrapper.innerHTML = fresh.innerHTML;

        /* The swapped-in cards carry empty Stamped badge hooks; ask the
           widget to fill them (see stamped-loader.liquid). */
        if (typeof window.stampedLoadBadges === 'function') window.stampedLoadBadges();

        if (push) window.history.pushState({ collectionUrl: url }, '', url);

        var results = document.querySelector('[data-results]');
        if (results) {
          var top = results.getBoundingClientRect().top + window.scrollY - 120;
          if (window.scrollY > top) window.scrollTo({ top: top, behavior: 'smooth' });
        }
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        /* Never leave the shopper stuck: fall back to a normal navigation. */
        window.location.href = url;
      })
      .finally(function () {
        inFlight = null;
        setLoading(false);
      });
  }

  function submitFilters(push) {
    var form = getForm();
    if (!form) return;
    render(urlFromForm(form), push !== false);
  }

  /* ------------------------------------------------------------------ events */

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target.name || !target.closest(SECTION_SELECTOR)) return;
    if (target.type === 'text') return; // debounced on input instead
    submitFilters();
  });

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (target.type !== 'text' || !target.name || !target.closest(SECTION_SELECTOR)) return;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(submitFilters, 600);
  });

  document.addEventListener('submit', function (event) {
    if (!event.target.matches(SECTION_SELECTOR)) return;
    event.preventDefault();
    submitFilters();
  });

  /* Price buckets, active-filter chips, clear all and the pager are links. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest(
      '.collection-rail__option--bucket, .collection-chip, .collection-chips__clear,' +
        ' .collection-pager__link, .collection-empty__clear'
    );
    if (!link || !link.closest(SECTION_SELECTOR)) return;
    if (link.tagName !== 'A' || !link.getAttribute('href')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    render(link.getAttribute('href'), true);
  });

  window.addEventListener('popstate', function () {
    render(window.location.pathname + window.location.search, false);
  });

  /* ---------------------------------------------------------- mobile drawer */

  function rail() {
    var form = getForm();
    return form ? form.querySelector('[data-rail]') : null;
  }

  function closeRail() {
    var form = getForm();
    if (!form) return;
    var r = form.querySelector('[data-rail]');
    var scrim = form.querySelector('[data-rail-scrim]');
    if (r) r.classList.remove('is-open');
    if (scrim) scrim.hidden = true;
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function (event) {
    var form = getForm();
    if (!form) return;

    if (event.target.closest('[data-rail-open]')) {
      var r = form.querySelector('[data-rail]');
      var scrim = form.querySelector('[data-rail-scrim]');
      if (r) r.classList.add('is-open');
      if (scrim) scrim.hidden = false;
      document.body.style.overflow = 'hidden';
      var close = form.querySelector('[data-rail-close]');
      if (close) close.focus();
      return;
    }

    if (event.target.closest('[data-rail-close]') || event.target.closest('[data-rail-scrim]')) {
      closeRail();
    }
  });

  document.addEventListener('keydown', function (event) {
    var r = rail();
    if (event.key === 'Escape' && r && r.classList.contains('is-open')) closeRail();
  });

  window.addEventListener(
    'resize',
    function () {
      var r = rail();
      if (window.innerWidth > 1000 && r && r.classList.contains('is-open')) closeRail();
    },
    { passive: true }
  );
})();
